import {
	LakeQueryResponse,
	NumberFormatOptions,
	Threshold,
	WidgetDisplay,
} from "../../../models/ReportBuilder";

const sv = (digits: number) =>
	new Intl.NumberFormat("sv-SE", { maximumFractionDigits: digits, minimumFractionDigits: 0 });
const svFixed = (digits: number) =>
	new Intl.NumberFormat("sv-SE", { maximumFractionDigits: digits, minimumFractionDigits: digits });

/** Numbers as the widget wants them: automatic decimals unless fixed, compact thousands/millions, prefix/suffix or unit. */
export function formatNumber(value: unknown, unit?: string, format?: NumberFormatOptions): string {
	if (value === null || value === undefined || value === "") return "–";
	const n = typeof value === "number" ? value : Number(value);
	if (Number.isNaN(n)) return String(value);
	let text: string;
	let compactSuffix = "";
	if (format?.compact && Math.abs(n) >= 1000) {
		const abs = Math.abs(n);
		const [div, label] = abs >= 1e9 ? [1e9, "mdr"] : abs >= 1e6 ? [1e6, "m"] : [1e3, "t"];
		const scaled = n / div;
		text = (
			format.decimals !== undefined ? svFixed(format.decimals) : sv(Math.abs(scaled) < 10 ? 1 : 0)
		).format(scaled);
		compactSuffix = label;
	} else if (format?.decimals !== undefined) {
		text = svFixed(Math.max(0, Math.min(6, format.decimals))).format(n);
	} else {
		const digits = Number.isInteger(n) ? 0 : Math.abs(n) < 100 ? 1 : 0;
		text = sv(digits).format(n);
	}
	const suffix = format?.suffix !== undefined && format.suffix !== "" ? format.suffix : unit;
	const tail = compactSuffix ? (suffix ? `${compactSuffix}${suffix}` : compactSuffix) : suffix;
	const body = tail ? `${text} ${tail}` : text;
	return format?.prefix ? `${format.prefix}${body}` : body;
}

export const formatPercent = (pct: number): string => `${pct > 0 ? "+" : ""}${sv(1).format(pct)} %`;

export const THRESHOLD_CLASSES: Record<Threshold["color"], { text: string; bg: string }> = {
	green: { text: "text-brand", bg: "bg-brand/10" },
	amber: { text: "text-warning", bg: "bg-warning/10" },
	red: { text: "text-danger", bg: "bg-danger/10" },
	gray: { text: "text-gray-500", bg: "bg-gray-100" },
};

/** The colour a value gets: the threshold with the highest `from` the value reaches, or none. */
export function thresholdColor(
	value: unknown,
	thresholds?: Threshold[]
): Threshold["color"] | undefined {
	if (!thresholds?.length) return undefined;
	const n = Number(value);
	if (!Number.isFinite(n)) return undefined;
	const hit = [...thresholds].sort((a, b) => b.from - a.from).find((t) => n >= t.from);
	return hit?.color;
}

export const isNumericColumn = (rows: Record<string, unknown>[], key: string): boolean =>
	rows.some((r) => typeof r[key] === "number");

const REST_LABEL = "Övriga";

/**
 * Sort, limit and fold rows as the display asks. Folding sums the numeric
 * columns of the rows beyond the limit into one "Övriga" row; comparison
 * rows are folded the same way so positions still line up.
 */
export function arrangeRows(
	data: LakeQueryResponse,
	display: WidgetDisplay | undefined,
	xKey: string | undefined
): LakeQueryResponse {
	const sort = display?.sort;
	const limit = display?.limit;
	if (!sort && !limit) return data;
	let indexes = data.rows.map((_, i) => i);
	if (sort && data.columns.includes(sort.key)) {
		const numeric = isNumericColumn(data.rows, sort.key);
		indexes = [...indexes].sort((a, b) => {
			const va = data.rows[a][sort.key];
			const vb = data.rows[b][sort.key];
			const cmp = numeric
				? Number(va ?? 0) - Number(vb ?? 0)
				: String(va ?? "").localeCompare(String(vb ?? ""), "sv");
			return sort.direction === "desc" ? -cmp : cmp;
		});
	}
	const pick = (rows: Record<string, unknown>[]) => indexes.map((i) => rows[i]).filter(Boolean);
	let rows = pick(data.rows);
	let cmpRows = data.comparison ? pick(data.comparison.rows) : undefined;
	if (limit && limit.n > 0 && rows.length > limit.n) {
		const fold = (all: Record<string, unknown>[]) => {
			const kept = all.slice(0, limit.n);
			if (!limit.rest) return kept;
			const rest = all.slice(limit.n);
			const folded: Record<string, unknown> = {};
			data.columns.forEach((c) => {
				folded[c] = isNumericColumn(all, c)
					? rest.reduce((sum, r) => sum + Number(r[c] ?? 0), 0)
					: c === xKey
					? REST_LABEL
					: null;
			});
			return [...kept, folded];
		};
		rows = fold(rows);
		cmpRows = cmpRows ? fold(cmpRows) : undefined;
	}
	return {
		...data,
		rows,
		rowCount: rows.length,
		comparison:
			data.comparison && cmpRows ? { ...data.comparison, rows: cmpRows } : data.comparison,
	};
}

/** Visible table columns in display order. */
export function visibleColumns(columns: string[], display?: WidgetDisplay): string[] {
	const hidden = new Set(display?.columns?.hidden ?? []);
	const order = display?.columns?.order ?? [];
	const ordered = [
		...order.filter((c) => columns.includes(c)),
		...columns.filter((c) => !order.includes(c)),
	];
	return ordered.filter((c) => !hidden.has(c));
}

/** Id columns the lake exposes → the dashboard page they open. The label column beside them becomes the link. */
export const DRILL_ROUTES: {
	idColumn: string;
	labelColumns: string[];
	path: (id: string) => string;
}[] = [
	{
		idColumn: "customer_id",
		labelColumns: ["customer_name", "kund", "customer"],
		path: (id) => `/customers/${id}`,
	},
	{
		idColumn: "consultant_id",
		labelColumns: ["consultant_name", "konsult", "consultant", "full_name"],
		path: (id) => `/consultants/${id}`,
	},
	{
		idColumn: "job_id",
		labelColumns: ["job_title", "uppdrag", "title", "order_number"],
		path: (id) => `/jobs/${id}`,
	},
	{
		idColumn: "contract_id",
		labelColumns: ["contract_name", "avtal", "contract"],
		path: (id) => `/contracts/${id}`,
	},
];

/** The in-app path a cell links to, when the row carries a matching id column. */
export function drillPath(
	row: Record<string, unknown>,
	column: string,
	columns: string[]
): string | undefined {
	for (const route of DRILL_ROUTES) {
		const id = row[route.idColumn];
		if (typeof id !== "string" || !id) continue;
		if (column === route.idColumn || route.labelColumns.includes(column.toLowerCase()))
			return route.path(id);
		// A row with exactly one id column and this is its first text column.
		const idColumns = columns.filter((c) => DRILL_ROUTES.some((r) => r.idColumn === c));
		if (idColumns.length === 1 && idColumns[0] === route.idColumn) {
			const firstText = columns.find((c) => c !== route.idColumn && typeof row[c] === "string");
			if (firstText === column) return route.path(id);
		}
	}
	return undefined;
}

const csvCell = (value: unknown): string => {
	if (value === null || value === undefined) return "";
	const text = typeof value === "number" ? String(value).replace(".", ",") : String(value);
	return /[";\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
};

/** Semicolon-separated with a BOM, so Excel in Swedish locale opens it directly. */
export function toCsv(columns: string[], rows: Record<string, unknown>[]): string {
	const lines = [
		columns.join(";"),
		...rows.map((r) => columns.map((c) => csvCell(r[c])).join(";")),
	];
	return `\uFEFF${lines.join("\r\n")}`;
}

export const toTsv = (columns: string[], rows: Record<string, unknown>[]): string =>
	[
		columns.join("\t"),
		...rows.map((r) =>
			columns.map((c) => (r[c] === null || r[c] === undefined ? "" : String(r[c]))).join("\t")
		),
	].join("\n");

export function downloadText(
	filename: string,
	text: string,
	type = "text/csv;charset=utf-8"
): void {
	const blob = new Blob([text], { type });
	const url = URL.createObjectURL(blob);
	const a = document.createElement("a");
	a.href = url;
	a.download = filename;
	document.body.appendChild(a);
	a.click();
	a.remove();
	setTimeout(() => URL.revokeObjectURL(url), 1000);
}

/** Rasterises the first SVG inside `root` (a recharts chart) and puts a PNG on the clipboard. */
export async function copyChartImage(root: HTMLElement): Promise<void> {
	const svg = root.querySelector("svg");
	if (!svg) throw new Error("Inget diagram att kopiera");
	const rect = svg.getBoundingClientRect();
	const clone = svg.cloneNode(true) as SVGSVGElement;
	clone.setAttribute("xmlns", "http://www.w3.org/2000/svg");
	clone.setAttribute("width", String(rect.width));
	clone.setAttribute("height", String(rect.height));
	const xml = new XMLSerializer().serializeToString(clone);
	const image = new Image();
	const scale = 2;
	await new Promise<void>((resolve, reject) => {
		image.onload = () => resolve();
		image.onerror = () => reject(new Error("Kunde inte rendera diagrammet"));
		image.src = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(xml)}`;
	});
	const canvas = document.createElement("canvas");
	canvas.width = rect.width * scale;
	canvas.height = rect.height * scale;
	const ctx = canvas.getContext("2d");
	if (!ctx) throw new Error("Canvas saknas");
	ctx.fillStyle = "#ffffff";
	ctx.fillRect(0, 0, canvas.width, canvas.height);
	ctx.scale(scale, scale);
	ctx.drawImage(image, 0, 0);
	const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, "image/png"));
	if (!blob) throw new Error("Kunde inte skapa bild");
	const ClipboardItemCtor = (
		window as unknown as { ClipboardItem?: new (items: Record<string, Blob>) => ClipboardItem }
	).ClipboardItem;
	if (!ClipboardItemCtor || !navigator.clipboard?.write)
		throw new Error("Webbläsaren kan inte kopiera bilder");
	await navigator.clipboard.write([new ClipboardItemCtor({ "image/png": blob })]);
}
